import { useRef } from "react";
import {
  motion,
  useInView,
  type Variant,
} from "framer-motion";

interface BlurFadeProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
  duration?: number;
  yOffset?: number;
  /**
   * @deprecated kept in the API for compatibility — the blur filter was
   * removed because `filter: blur()` transitions caused visible flashing on
   * mobile (compositing-layer churn + per-frame GPU repaint). The component
   * now relies solely on opacity + translateY for the fade-up cascade.
   */
  blur?: string;
  inView?: boolean;
  inViewMargin?: string;
}

export function BlurFade({
  children,
  className,
  style,
  delay = 0,
  duration = 0.4,
  yOffset = 6,
  inView = false,
  inViewMargin = "0px",
}: BlurFadeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inViewResult = useInView(ref, { once: true, margin: inViewMargin as any });
  const isInView = !inView || inViewResult;

  // Always ship visible. Animate translateY only — no opacity transition.
  //
  // Previously this component shipped opacity:0 into the SSR HTML (via
  // framer-motion serializing initial="hidden"). On mobile that meant 100+
  // wrapped elements rendered invisible until hydration + IntersectionObserver
  // callbacks fired, which on slow scrolls produced the visible "flashing
  // batches of content popping in" symptom. The earlier 300px-bottom margin
  // was meant to compensate for this and made it worse — anything farther
  // than 300px below the fold stayed at opacity:0 until the user scrolled it
  // close enough, then animated under their thumb.
  //
  // The fix: never ship opacity:0. The fade-up is now a translateY-only
  // animation (y:6 → y:0) which is compositor-cheap and doesn't visually
  // flash since the element is fully opaque throughout.
  const hidden: Variant = {
    y: yOffset,
    opacity: 1,
  };
  const visible: Variant = {
    y: 0,
    opacity: 1,
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden,
        visible,
      }}
      transition={{
        delay: 0.04 + delay,
        duration,
        ease: "easeOut",
      }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}
